import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

export interface RecipeGuideEquipment {
  id: number
  name: string
  description: string
  instructions: string
  imageUrl: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface RecipeGuideEquipmentResponse {
  data: RecipeGuideEquipment
}

export interface RecipeGuideEquipmentsResponse {
  data: RecipeGuideEquipment[]
  meta: {
    total: number
    pages: number
  }
}

export interface RecipeGuideEquipmentStats {
  totalEquipments: number
  equipmentsWithImage: number
  activeEquipments: number
  inactiveEquipments: number
}

interface RecipeGuideEquipmentQueryOptions {
  take?: number
  skip?: number
  sortBy?: string
  filters?: Record<string, any>
}

interface RecipeGuideEquipmentInitialState {
  equipments: RecipeGuideEquipment[]
  equipment: RecipeGuideEquipment | null
  stats: RecipeGuideEquipmentStats
  loading: boolean
  meta: RecipeGuideEquipmentsResponse['meta']
  error: any
  success: string | null
}

const initialState: RecipeGuideEquipmentInitialState = {
  equipments: [],
  equipment: null,
  stats: {
    totalEquipments: 0,
    equipmentsWithImage: 0,
    activeEquipments: 0,
    inactiveEquipments: 0
  },
  loading: false,
  meta: {
    total: 0,
    pages: 0
  },
  error: null,
  success: null
}

export const getRecipeGuideEquipments = createAsyncThunk<
  RecipeGuideEquipmentsResponse,
  RecipeGuideEquipmentQueryOptions,
  { rejectValue: any; extra: { httpClient: any } }
>(
  'gygRecipeGuideEquipments/getRecipeGuideEquipments',
  async ({ take = 10, sortBy = 'name', skip = 0, filters = {} }, { rejectWithValue, extra }) => {
    try {
      const response = await extra.httpClient.get(`recipe-guide-equipments`, {
        params: { take, sortBy, filters, skip }
      })

      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const createRecipeGuideEquipment = createAsyncThunk<
  RecipeGuideEquipmentResponse,
  FormData,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideEquipments/createRecipeGuideEquipment', async (formData, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.post('recipe-guide-equipments', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const updateRecipeGuideEquipment = createAsyncThunk<
  RecipeGuideEquipmentResponse,
  { id: number; data: FormData },
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideEquipments/updateRecipeGuideEquipment', async ({ id, data }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.put(`recipe-guide-equipments/${id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getRecipeGuideEquipment = createAsyncThunk<
  RecipeGuideEquipmentResponse,
  number,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideEquipments/getRecipeGuideEquipment', async (id, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`recipe-guide-equipments/${id}`)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const deleteRecipeGuideEquipment = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideEquipments/deleteRecipeGuideEquipment', async (id, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.delete(`recipe-guide-equipments/${id}`)

    return { id }
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getRecipeGuideEquipmentStats = createAsyncThunk<
  { data: RecipeGuideEquipmentStats },
  void,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideEquipments/getRecipeGuideEquipmentStats', async (_, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get('recipe-guide-equipments/stats')

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

const gygRecipeGuideEquipmentSlice = createSlice({
  name: 'gygRecipeGuideEquipments',
  initialState,
  reducers: {
    resetSuccessAndError: state => {
      state.success = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder.addCase(getRecipeGuideEquipments.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipeGuideEquipments.fulfilled, (state, action) => {
      state.loading = false
      state.error = null
      state.success = null
      state.equipments = action.payload.data
      state.meta = action.payload.meta
    })
    builder.addCase(getRecipeGuideEquipments.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch recipe guide equipments'
    })

    builder.addCase(createRecipeGuideEquipment.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(createRecipeGuideEquipment.fulfilled, (state, action) => {
      state.loading = false
      state.equipments.unshift(action.payload.data)
      state.meta.total += 1
      state.success = 'Recipe guide equipment created successfully'
    })
    builder.addCase(createRecipeGuideEquipment.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to create recipe guide equipment'
    })

    builder.addCase(updateRecipeGuideEquipment.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(updateRecipeGuideEquipment.fulfilled, state => {
      state.loading = false
      state.success = 'Recipe guide equipment updated successfully'
    })
    builder.addCase(updateRecipeGuideEquipment.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to update recipe guide equipment'
    })

    builder.addCase(getRecipeGuideEquipment.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipeGuideEquipment.fulfilled, (state, action) => {
      state.loading = false
      state.equipment = action.payload.data
    })
    builder.addCase(getRecipeGuideEquipment.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch recipe guide equipment'
    })

    builder.addCase(deleteRecipeGuideEquipment.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(deleteRecipeGuideEquipment.fulfilled, (state, action) => {
      state.loading = false
      state.equipments = state.equipments.filter(e => e.id !== action.payload.id)
      state.meta.total -= 1
      state.success = 'Recipe guide equipment deleted successfully'
    })
    builder.addCase(deleteRecipeGuideEquipment.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to delete recipe guide equipment'
    })

    builder.addCase(getRecipeGuideEquipmentStats.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipeGuideEquipmentStats.fulfilled, (state, action) => {
      state.loading = false
      state.stats = action.payload.data
    })
    builder.addCase(getRecipeGuideEquipmentStats.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch recipe guide equipment stats'
    })
  }
})

export const { resetSuccessAndError } = gygRecipeGuideEquipmentSlice.actions
export default gygRecipeGuideEquipmentSlice.reducer
